import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, BookOpen, Compass, Landmark, Microscope, Camera, Newspaper, Mail } from "lucide-react";
import api, { FOUNDATION, fileUrl } from "@/lib/api";
import { SectionTitle } from "@/components/Layout";
import { ArticleCard } from "@/components/ArticleCard";
import { BookCard } from "@/components/BookCard";
import { MagazineCard } from "@/components/MagazineCard";

const iconMap = { landmark: Landmark, compass: Compass, microscope: Microscope, camera: Camera, book: BookOpen };

export default function Home() {
  const [data, setData] = useState({ articles: [], books: [], magazines: [], initiatives: [], gallery: [] });

  useEffect(() => {
    window.scrollTo(0, 0);
    Promise.all([
      api.get("/articles"), api.get("/books"), api.get("/magazines"),
      api.get("/initiatives"), api.get("/gallery"),
    ]).then(([a, b, m, i, g]) =>
      setData({ articles: a.data, books: b.data, magazines: m.data, initiatives: i.data, gallery: g.data })
    ).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section data-testid="hero-section" className="sunburst-bg text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center relative z-10">
          <img
            src={FOUNDATION.logo}
            alt="Tourist Sandesh Foundation"
            className="w-28 h-28 sm:w-32 sm:h-32 rounded-full object-cover mx-auto ring-4 ring-amber-400/60 shadow-2xl fade-up"
          />
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 fade-up">{FOUNDATION.name}</h1>
          <p className="font-eng text-amber-400 tracking-widest uppercase text-sm mt-2">{FOUNDATION.nameEng}</p>
          <div className="w-28 h-1 bg-amber-500 mx-auto rounded-full my-6" />
          <h2 className="font-serif-deva text-xl sm:text-2xl lg:text-3xl max-w-3xl mx-auto leading-relaxed text-slate-100">
            "पर्यटन, संस्कृति एवं विरासत के संदेश को जन-जन तक पहुँचाना"
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto mt-5 text-base sm:text-lg leading-relaxed">
            पर्यटन, संस्कृति, विरासत, शोध, जागरूकता एवं दस्तावेज़ीकरण के क्षेत्र में कार्यरत एक
            सांस्कृतिक एवं शोध संस्था। यहाँ आप लेख, शोध सामग्री, पुस्तकें एवं प्रकाशन पढ़ एवं
            डाउनलोड कर सकते हैं।
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-9">
            <Link to="/about" data-testid="hero-explore-button">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white px-8 h-12 text-base gap-2">
                अधिक जानें <ArrowLeft size={18} className="rotate-180" />
              </Button>
            </Link>
            <Link to="/library" data-testid="hero-books-button">
              <Button size="lg" variant="outline" className="border-white/40 bg-white/5 text-white hover:bg-white/15 px-8 h-12 text-base gap-2">
                <BookOpen size={18} /> हमारी पुस्तकें देखें
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ABOUT INTRO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-amber-700 font-medium tracking-wide">हमारे बारे में</span>
            <h2 className="font-display text-3xl sm:text-4xl text-[#0a2540] mt-2 mb-5">फाउंडेशन का परिचय</h2>
            <p className="text-slate-600 prose-deva">
              टूरिस्ट संदेश फाउंडेशन की स्थापना वर्ष {FOUNDATION.foundedYear} में हुई। इससे पूर्व
              वर्ष {FOUNDATION.magazineYear} से "टूरिस्ट संदेश" पत्रिका का प्रकाशन आरंभ हुआ, जो
              पर्यटन, संस्कृति एवं विरासत को समर्पित रही है। फाउंडेशन का उद्देश्य उत्तराखंड एवं
              भारत की समृद्ध सांस्कृतिक धरोहर, पर्यटन स्थलों एवं परंपराओं का दस्तावेज़ीकरण, शोध
              एवं प्रचार-प्रसार करना है।
            </p>
            <Link to="/about">
              <Button variant="outline" className="mt-6 border-amber-400 text-amber-700 hover:bg-amber-50 gap-2">
                पूरा परिचय पढ़ें <ArrowLeft size={16} className="rotate-180" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: Landmark, t: "संस्कृति एवं विरासत", d: "धरोहर का संरक्षण" },
              { icon: Microscope, t: "शोध एवं दस्तावेज़ीकरण", d: "गहन अध्ययन" },
              { icon: Newspaper, t: "मासिक प्रकाशन", d: `${FOUNDATION.magazineYear} से निरंतर` },
              { icon: Compass, t: "पर्यटन जागरूकता", d: "जन-जागरण" },
            ].map((c, idx) => (
              <div key={idx} className="bg-white border border-amber-200/60 rounded-xl p-5 shadow-sm card-hover">
                <div className="w-12 h-12 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600 mb-3">
                  <c.icon size={24} />
                </div>
                <h3 className="font-serif-deva text-lg text-[#0a2540]">{c.t}</h3>
                <p className="text-sm text-slate-500 mt-1">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INITIATIVES */}
      {data.initiatives.length > 0 && (
        <section className="bg-white border-y border-amber-200/50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <SectionTitle sub="फाउंडेशन द्वारा संचालित प्रमुख कार्य एवं अभियान">हमारी प्रमुख पहल</SectionTitle>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.initiatives.slice(0, 6).map((it) => {
                const Icon = iconMap[it.icon] || Landmark;
                return (
                  <div key={it.id} className="bg-ivory border border-amber-200/60 rounded-xl p-6 card-hover" style={{ background: "#faf9f5" }}>
                    <div className="w-12 h-12 rounded-lg bg-[#0a2540] flex items-center justify-center text-amber-400 mb-4">
                      <Icon size={22} />
                    </div>
                    <h3 className="font-serif-deva text-xl text-[#0a2540]">{it.title}</h3>
                    <p className="text-sm text-slate-600 mt-2 line-clamp-3">{it.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* LATEST ARTICLES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <SectionTitle sub="पर्यटन, संस्कृति एवं विरासत पर नवीनतम लेख">नवीनतम लेख</SectionTitle>
        {data.articles.length > 0 ? (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.articles.slice(0, 3).map((a) => <ArticleCard key={a.id} article={a} />)}
            </div>
            <div className="text-center mt-10">
              <Link to="/articles"><Button variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50">सभी लेख देखें</Button></Link>
            </div>
          </>
        ) : (
          <p className="text-center text-slate-400 py-8">शीघ्र ही नवीनतम लेख प्रकाशित किए जाएंगे।</p>
        )}
      </section>

      {/* MAGAZINE FEATURE */}
      {data.magazines.length > 0 && (
        <section className="bg-[#0a2540] py-20 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-amber-400 font-medium tracking-wide">टूरिस्ट संदेश मासिक</span>
              <h2 className="font-display text-3xl sm:text-4xl mt-2 mb-4">नवीनतम अंक</h2>
              <p className="text-slate-300 prose-deva">
                "टूरिस्ट संदेश" मासिक पत्रिका वर्ष {FOUNDATION.magazineYear} से निरंतर प्रकाशित हो
                रही है। प्रत्येक अंक में पर्यटन, संस्कृति, विरासत एवं शोध से जुड़ी महत्वपूर्ण
                सामग्री शामिल होती है। पुराने एवं नवीनतम सभी अंक यहाँ पढ़ें एवं डाउनलोड करें।
              </p>
              <Link to="/magazine"><Button className="mt-6 bg-amber-500 hover:bg-amber-600 gap-2">सभी अंक देखें <ArrowLeft size={16} className="rotate-180" /></Button></Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {data.magazines.slice(0, 3).map((m) => <MagazineCard key={m.id} issue={m} />)}
            </div>
          </div>
        </section>
      )}

      {/* LATEST BOOKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <SectionTitle sub="फाउंडेशन द्वारा प्रकाशित एवं संग्रहित पुस्तकें">नवीनतम पुस्तकें / प्रकाशन</SectionTitle>
        {data.books.length > 0 ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {data.books.slice(0, 4).map((b) => <BookCard key={b.id} book={b} />)}
            </div>
            <div className="text-center mt-10">
              <Link to="/library"><Button variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50">पूरा पुस्तकालय देखें</Button></Link>
            </div>
          </>
        ) : (
          <p className="text-center text-slate-400 py-8">शीघ्र ही पुस्तकें एवं प्रकाशन जोड़े जाएंगे।</p>
        )}
      </section>

      {/* GALLERY TEASER */}
      {data.gallery.length > 0 && (
        <section className="bg-white border-y border-amber-200/50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <SectionTitle sub="फाउंडेशन की गतिविधियों एवं कार्यक्रमों की झलकियाँ">फोटो गैलरी</SectionTitle>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {data.gallery.slice(0, 8).map((g) => (
                <div key={g.id} className="aspect-square rounded-lg overflow-hidden card-hover">
                  <img src={fileUrl(g.image_url)} alt={g.title} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/gallery"><Button variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50">पूरी गैलरी देखें</Button></Link>
            </div>
          </div>
        </section>
      )}

      {/* CONTACT CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="bg-gradient-to-br from-[#0a2540] to-[#061729] rounded-2xl p-10 sm:p-14 text-center text-white relative overflow-hidden">
          <Mail size={40} className="mx-auto text-amber-400 mb-4" />
          <h2 className="font-display text-3xl sm:text-4xl mb-3">हमसे जुड़ें</h2>
          <p className="text-slate-300 max-w-xl mx-auto mb-7">
            शोध, प्रकाशन, सहयोग अथवा किसी भी जानकारी हेतु हमसे संपर्क करें।
          </p>
          <Link to="/contact"><Button size="lg" className="bg-amber-500 hover:bg-amber-600 px-8 h-12">संपर्क करें</Button></Link>
        </div>
      </section>
    </div>
  );
}
